// src/Pages/Invoice.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Invoice.module.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import { useToast } from '../Components/ui/Toast.jsx';

const Invoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [invoiceData, setInvoiceData] = useState({
    ticketData: null,
    travelDetails: null,
    userDataPerSeat: null,
    selectedSeats: null,
    reservationCode: null,
    paymentMethod: null,
  });

  useEffect(() => {
    const ticketData = location.state?.ticketData;
    const travelDetails = location.state?.travelDetails;
    const userDataPerSeat = location.state?.userDataPerSeat;
    const selectedSeats = location.state?.selectedSeats;
    const reservationCode = location.state?.reservationCode;
    const paymentMethod = location.state?.paymentMethod;

    if (
      ticketData &&
      travelDetails &&
      userDataPerSeat &&
      selectedSeats &&
      reservationCode
    ) {
      setInvoiceData({
        ticketData,
        travelDetails,
        userDataPerSeat,
        selectedSeats,
        reservationCode,
        paymentMethod,
      });
    } else {
      const savedInvoice = localStorage.getItem('currentInvoice');
      if (savedInvoice) {
        setInvoiceData(JSON.parse(savedInvoice));
      } else {
        showToast('No se recibieron datos de reserva. Redirigiendo a rutas de bus…', { type: 'warning' });
        navigate('/busroutes');
      }
    }
  }, [location, navigate, showToast]);

  const handleDownloadInvoice = () => {
    const invoiceElement = document.getElementById('invoice');

    // Configurar opciones para mejor calidad
    const options = {
      scale: 2, // Mejor resolución
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    };

    html2canvas(invoiceElement, options).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Calcular dimensiones para ajustar al PDF
      const imgWidth = 190;
      const pageHeight = 277;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;

      // Agregar primera página
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Si el contenido es más largo, agregar páginas adicionales
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generar nombre del archivo basado en código de reserva
      const fileName = invoiceData.reservationCode
        ? `Ticket_${invoiceData.reservationCode}.pdf`
        : `Factura_${new Date().getTime()}.pdf`;

      pdf.save(fileName);
    }).catch((error) => {
      showToast('Hubo un error al generar el PDF. Por favor intenta de nuevo.', { type: 'error' });
    });
  };

  if (!invoiceData.travelDetails) {
    return null;
  }

  // Detectar si viene desde UserProfile (formato simplificado) o desde PurchaseConfirmation
  const isFromUserProfile = !invoiceData.userDataPerSeat;

  const unitPrice = Number(invoiceData.travelDetails.price || invoiceData.travelDetails.Price);
  const seatCount = invoiceData.selectedSeats?.length || 1;
  const totalPrice = invoiceData.totalAmount || (unitPrice * seatCount);

  return (
    <div className={styles.invoiceContainer} id="invoice">
      <h2>Reserva</h2>

      {/* ----------------------------------------------------
          1) Aquí mostramos el Código de Viaje y, a su derecha,
             un QR pequeño que codifica el reservationCode,
             con el texto del reservationCode justo debajo.
      ---------------------------------------------------- */}
      <div className={styles.codeAndQrContainer}>
        <p className={styles.codeViaje}>
          <strong>Código de Viaje:</strong> {invoiceData.travelDetails.codigo || invoiceData.reservationCode}
        </p>
        <div className={styles.smallQrWrapper}>
          <QRCodeCanvas
            value={invoiceData.reservationCode}
            size={80}
            level="H"
          />
          <p className={styles.reservationText}>
            {invoiceData.reservationCode}
          </p>
        </div>
      </div>

      {/* ----------------------------------------------------
          2) Detalles del viaje
      ---------------------------------------------------- */}
      <div className={styles.detailsContainer}>
        <p>
          <strong>Fecha del Viaje:</strong>{' '}
          {(() => {
            const raw = invoiceData.travelDetails.date || invoiceData.travelDetails.Departure_date;
            if (!raw) return 'Por confirmar';
            const d = String(raw).slice(0, 10);
            const [y, m, day] = d.split('-');
            return `${day}/${m}/${y}`;
          })()}
        </p>
        <p>
          <strong>Hora de Salida:</strong>{' '}
          {invoiceData.travelDetails.departing_time || invoiceData.travelDetails.Departure_time || '—'}
        </p>
        <p>
          <strong>Origen:</strong> {invoiceData.travelDetails.departure || invoiceData.travelDetails.Origin}
        </p>
        <p>
          <strong>Destino:</strong> {invoiceData.travelDetails.arrival || invoiceData.travelDetails.Destination}
        </p>
        {invoiceData.travelDetails.estimated_duration && (
          <p>
            <strong>Duración:</strong>{' '}
            {invoiceData.travelDetails.estimated_duration} horas
          </p>
        )}
        <p>
          <strong>Precio por Asiento:</strong> $
          {unitPrice}
        </p>
        <p>
          <strong>Total a Pagar:</strong> ${totalPrice.toFixed(2)}
        </p>
      </div>

      {/* ----------------------------------------------------
          3) Datos del usuario (solo si viene desde PurchaseConfirmation)
      ---------------------------------------------------- */}
      {!isFromUserProfile && invoiceData.userDataPerSeat && invoiceData.selectedSeats[0] && (
        <>
          <div className={styles.userDataContainer}>
            <h3>Datos de Usuario</h3>
            <p>
              <strong>Nombre:</strong>{' '}
              {invoiceData.userDataPerSeat[invoiceData.selectedSeats[0]].nombre}
            </p>
            <p>
              <strong>Correo Electrónico:</strong>{' '}
              {invoiceData.userDataPerSeat[invoiceData.selectedSeats[0]].correo}
            </p>
            <p>
              <strong>Teléfono:</strong>{' '}
              {invoiceData.userDataPerSeat[invoiceData.selectedSeats[0]].telefono}
            </p>
          </div>

          <p>
            <strong>Cédula:</strong>{' '}
            {invoiceData.userDataPerSeat[invoiceData.selectedSeats[0]].identificacion}
          </p>
        </>
      )}

      {/* ----------------------------------------------------
          4) Asientos seleccionados
      ---------------------------------------------------- */}
      {invoiceData.selectedSeats && invoiceData.selectedSeats.length > 0 && (
        <div className={styles.seatsContainer}>
          <h3>Asientos Seleccionados</h3>
          <p>{invoiceData.selectedSeats.join(', ')}</p>
        </div>
      )}

      {/* ----------------------------------------------------
          5) Detalles de pasajeros (tabla)
      ---------------------------------------------------- */}
      {!isFromUserProfile && invoiceData.userDataPerSeat && invoiceData.selectedSeats && (
        <div className={styles.passengersContainer}>
          <h3>Detalles de Pasajeros</h3>
          <table className={styles.passengersTable}>
            <thead>
              <tr>
                <th>Asiento</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Teléfono</th>
                <th>Cedula</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.selectedSeats.map((seatId, idx) => {
                const pData = invoiceData.userDataPerSeat[seatId];
                return (
                  <tr key={idx}>
                    <td style={{ textAlign: 'center' }}>{seatId}</td>
                    <td>{pData.nombre}</td>
                    <td>{pData.correo}</td>
                    <td>{pData.telefono}</td>
                    <td>{pData.identificacion}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ----------------------------------------------------
          6) Recordatorio de pago presencial
      ---------------------------------------------------- */}
      <p className={styles.paymentNotice}>
        <br />
        Muestra el <strong>código de viaje</strong> al vendedor para confirmar tu
        reserva (o escanea el QR).
      </p>

      {/* ----------------------------------------------------
          7) Botón para descargar PDF
      ---------------------------------------------------- */}
      <button
        className={`${styles.downloadButton} noPrint`}
        onClick={handleDownloadInvoice}
      >
        Descargar Factura en PDF
      </button>
    </div>
  );
};

export default Invoice;
